import { getJwtToken, refreshKmJwtToken } from "@/lib/auth-client";
import { ragUrl } from "@/lib/api-config";
import { feedSseBuffer } from "@/lib/rag-sse";

async function getJwtTokenWithRetry(): Promise<string | null> {
  const token = await getJwtToken();
  if (token) {
    return token;
  }

  await new Promise((resolve) => setTimeout(resolve, 100));
  return getJwtToken();
}

function parseRagErrorMessage(data: unknown, status: number): string {
  const errorLabel =
    typeof data === "object" && data && "error" in data
      ? String((data as { error: string }).error)
      : null;
  const errorDetail =
    typeof data === "object" && data && "message" in data
      ? String((data as { message: string }).message)
      : null;

  if (status === 401) {
    return "Your session expired. Please sign in again.";
  }

  return errorDetail && errorDetail !== errorLabel
    ? errorDetail
    : errorLabel ?? errorDetail ?? `Request failed (${status})`;
}

export class RagStreamNotSupportedError extends Error {
  constructor() {
    super("RAG chat streaming is not available");
    this.name = "RagStreamNotSupportedError";
  }
}

export interface RagSseHandlers {
  onUser?: (data: unknown) => void;
  onStatus?: (data: unknown) => void;
  onSources?: (data: unknown) => void;
  onDelta?: (data: unknown) => void;
  onDone?: (data: unknown) => void;
}

async function ragRequest(path: string, options: RequestInit, token: string): Promise<Response> {
  const isFormData = options.body instanceof FormData;

  return fetch(ragUrl(path), {
    ...options,
    credentials: "include",
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
}

export async function getRagAuthHeaders(): Promise<HeadersInit> {
  const token = await getJwtTokenWithRetry();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

export async function ragFetch<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  let token = await getJwtTokenWithRetry();
  if (!token) {
    throw new Error("Please log in to access KM. Your session may have expired.");
  }

  let response = await ragRequest(path, options, token);
  let data = await response.json().catch(() => ({}));

  if (response.status === 401) {
    token = await refreshKmJwtToken();
    if (token) {
      response = await ragRequest(path, options, token);
      data = await response.json().catch(() => ({}));
    }
  }

  if (!response.ok) {
    throw new Error(parseRagErrorMessage(data, response.status));
  }

  return data as T;
}

/**
 * POST JSON and consume RAG chat SSE until the stream ends.
 * Throws {@link RagStreamNotSupportedError} when the stream route returns 404.
 */
export async function consumeRagSse(
  path: string,
  body: unknown,
  handlers: RagSseHandlers,
  signal?: AbortSignal,
): Promise<void> {
  let token = await getJwtTokenWithRetry();
  if (!token) {
    throw new Error("Please log in to access KM. Your session may have expired.");
  }

  const requestOptions: RequestInit = {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  };

  let response = await ragRequest(path, requestOptions, token);

  if (response.status === 401) {
    token = await refreshKmJwtToken();
    if (token) {
      response = await ragRequest(path, requestOptions, token);
    }
  }

  if (response.status === 404) {
    throw new RagStreamNotSupportedError();
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(parseRagErrorMessage(data, response.status));
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  const dispatch = (event: string, data: unknown) => {
    switch (event) {
      case "user":
        handlers.onUser?.(data);
        break;
      case "status":
        handlers.onStatus?.(data);
        break;
      case "sources":
        handlers.onSources?.(data);
        break;
      case "delta":
        handlers.onDelta?.(data);
        break;
      case "done":
        handlers.onDone?.(data);
        break;
      default:
        break;
    }
  };

  try {
    while (true) {
      if (signal?.aborted) {
        await reader.cancel();
        break;
      }

      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      buffer = feedSseBuffer(buffer, ({ event, data }) => dispatch(event, data));
    }

    if (buffer.trim()) {
      feedSseBuffer(`${buffer}\n\n`, ({ event, data }) => dispatch(event, data));
    }
  } finally {
    reader.releaseLock();
  }
}

export async function ragUpload<T = unknown>(
  path: string,
  formData: FormData
): Promise<T> {
  return ragFetch<T>(path, {
    method: "POST",
    body: formData,
  });
}

export async function fetchDocumentFile(documentId: string): Promise<Blob> {
  return fetchRagAuthenticatedFile(`/api/documents/${documentId}/file`);
}

export async function fetchRagAuthenticatedFile(path: string): Promise<Blob> {
  const token = await getJwtTokenWithRetry();
  if (!token) {
    throw new Error("Please log in to access KM. Your session may have expired.");
  }

  const response = await fetch(ragUrl(path), {
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(parseRagErrorMessage(data, response.status));
  }

  return response.blob();
}

export async function fetchDocumentVersionFile(
  documentId: string,
  versionId: string,
): Promise<Blob> {
  return fetchRagAuthenticatedFile(
    `/api/documents/${documentId}/versions/${versionId}/file`,
  );
}

export async function fetchThreadAttachmentFile(
  threadId: string,
  attachmentId: string,
): Promise<Blob> {
  const token = await getJwtTokenWithRetry();
  if (!token) {
    throw new Error("Please log in to access KM. Your session may have expired.");
  }

  const response = await fetch(
    ragUrl(`/api/threads/${threadId}/attachments/${attachmentId}/file`),
    {
      credentials: "include",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(parseRagErrorMessage(data, response.status));
  }

  return response.blob();
}
